import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { AssinaturaMalgo } from './componentes/assinatura';
import { useTranslation } from 'react-i18next';
import { ErroRequisicao } from './nucleo/api';
import { ProvedorSessao, useSessaoQuery } from './nucleo/sessao';
import { Shell } from './layout/Shell';
import { Inicio } from './paginas/Inicio';
import { MinhaConta } from './paginas/MinhaConta';
import { Notificacoes } from './paginas/Notificacoes';
import { RedirecionarAutomacoes } from './paginas/Automacoes';
import { AprovarAcao } from './paginas/AprovarAcao';
import { AutorizarOAuth } from './paginas/AutorizarOAuth';
import { PopupNovidades } from './novidades/PopupNovidades';
import { useConexaoTempoReal } from './nucleo/tempo-real';
import { PaginaSegundoFatorObrigatorio, PaginaTrocaSenhaObrigatoria } from './paginas/conta-seguranca';
import { Secao } from './paginas/Secao';
import { EscolhaSubsistema } from './paginas/EscolhaSubsistema';
import { PaginaExterna } from './paginas/Externo';
import { PAGINAS_CASCA, PaginaCasca } from './paginas/PaginaCasca';
import { esquecerEstiloDoWorkspace } from './nucleo/saida';
import { ProvedorSubsistema } from './nucleo/subsistemas';
import { ConfirmarEmail, Convite, EsqueciSenha, Login, RedefinirSenha, Saida } from './paginas/publicas';
import { FundoPublico } from './paginas/fundo-publico';
import { Carregando, Erro, Vazio } from './componentes/estados';

function Protegido() {
  const q = useSessaoQuery();
  const local = useLocation();
  // rcor-4 SEG4-B1: carga nova com a sessão vencida; o login de quem vem depois não leva as cores do workspace anterior.
  const semSessao = q.error instanceof ErroRequisicao && q.error.status === 401;
  useEffect(() => {
    if (semSessao) esquecerEstiloDoWorkspace();
  }, [semSessao]);
  if (q.isPending) return <Carregando />;
  if (q.isError) {
    if (q.error instanceof ErroRequisicao && q.error.status === 401) {
      const voltar = local.pathname === '/' ? '' : `?voltar=${encodeURIComponent(local.pathname + local.search)}`;
      return <Navigate to={`/login${voltar}`} replace />;
    }
    return (
      <div className="flex min-h-dvh items-center justify-center bg-fundo px-4">
        <div className="w-full max-w-md rounded-2xl border border-borda bg-sup-1 p-6">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-tinta-1">
              <AssinaturaMalgo />
            </span>
            <span className="sr-only">MalgoOS</span>
          </div>
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        </div>
      </div>
    );
  }
  // Fase 2 (W4): TOTP obrigatório sem configurar e carência vencida; a sessão só alcança a configuração (ou sair).
  // val1 (28/10): senha provisória (acesso sem e-mail criado pelo administrador): só a troca de senha (ou sair).
  if (q.data.seguranca?.troca_senha) {
    return (
      <ProvedorSessao dados={q.data}>
        <PaginaTrocaSenhaObrigatoria />
      </ProvedorSessao>
    );
  }
  if (q.data.seguranca?.mfa_restrita) {
    return (
      <ProvedorSessao dados={q.data}>
        <PaginaSegundoFatorObrigatorio />
      </ProvedorSessao>
    );
  }
  return (
    <ProvedorSessao dados={q.data}>
      <TempoRealDaSessao workspaceId={q.data.workspace.id} />
      {/* D1 (sub-sistemas): a escolha de empresa e sub-sistema; sem a configuração ligada, não muda nada. */}
      <ProvedorSubsistema>
        <Routes>
          <Route element={<Shell rotas={rotasDoConteudo} />}>{rotasDoConteudo()}</Route>
        </Routes>
      </ProvedorSubsistema>
    </ProvedorSessao>
  );
}

/**
 * As telas do conteúdo autenticado, dentro do Shell. Lote 19 (item 13, tela dividida): as mesmas rotas montam o painel ao lado,
 * no roteador próprio dele.
 */
function rotasDoConteudo() {
  return (
    <>
      <Route index element={<Inicio />} />
      <Route path="subsistemas" element={<EscolhaSubsistema />} />
      {/* FUN 2026-10-14 (cofre embutido): link externo que abre dentro do MalgoOS. */}
      <Route path="externo/:codigo" element={<PaginaExterna />} />
      <Route path="conta/*" element={<MinhaConta />} />
      <Route path="conta/notificacoes" element={<Notificacoes />} />
      {/* B8 (lote 09/10): o endereço antigo das Automações; desde a U7 (15/10) leva ao painel do módulo. */}
      <Route path="automacoes" element={<RedirecionarAutomacoes />} />
      {/* Fase 2 (W11): o push do assistente abre aqui (aprovar ou recusar a ação preparada). */}
      <Route path="aprovar/:id" element={<AprovarAcao />} />
      {/* Lote 19/10: consentimento do OAuth do MCP (o /oauth/authorize da API manda para cá). */}
      <Route path="autorizar" element={<AutorizarOAuth />} />
      {/* CRM no novo desenho (Fase 2): páginas próprias dos sub-sistemas (`paginasCasca`), fora da seção de abas. */}
      {PAGINAS_CASCA.map(({ slug, pagina }) => (
        <Route key={`${slug}.${pagina.chave}`} path={`${pagina.caminho}/*`} element={<PaginaCasca slug={slug} pagina={pagina} />} />
      ))}
      <Route path=":menu/:modulo/*" element={<Secao />} />
      <Route path=":menu" element={<Secao />} />
      <Route path="*" element={<NaoEncontrado />} />
    </>
  );
}

/** Fase 2 (W3): SSE da sessão (um por aba, reaberto na troca de workspace) e o pop-up de novidades depois do tour. */
function TempoRealDaSessao({ workspaceId }: { workspaceId: string }) {
  useConexaoTempoReal(workspaceId);
  return <PopupNovidades />;
}

function NaoEncontrado() {
  const { t } = useTranslation();
  return <Vazio titulo={t('estados.naoEncontrado')} texto="" />;
}

export function App() {
  return (
    <Routes>
      {/* Fase 2 (W32): páginas públicas sobre o mesmo fundo (foto do farol, abertura e splash), montado uma vez entre elas. */}
      <Route element={<FundoPublico />}>
        <Route path="/login" element={<Login />} />
        <Route path="/esqueci-senha" element={<EsqueciSenha />} />
        <Route path="/redefinir-senha" element={<RedefinirSenha />} />
        <Route path="/confirmar-email" element={<ConfirmarEmail />} />
        <Route path="/convite/:token" element={<Convite />} />
        <Route path="/saida" element={<Saida />} />
      </Route>
      <Route path="/*" element={<Protegido />} />
    </Routes>
  );
}
